import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'

export const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="mb-4 text-blue-600 hover:text-blue-800"
          >
            ← Back to Home
          </Button>
          
          <div className="text-center mb-6">
            <div className="text-3xl font-bold text-gray-900 mb-2">
              Strat<span className="text-blue-600">IQ</span><span className="text-cyan-600">X</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Privacy Policy</h1>
            <p className="text-gray-600">Preview Version - Effective Date: {new Date().toLocaleDateString()}</p>
          </div>
        </div>

        <Card className="shadow-lg border-0 bg-white">
          <CardContent className="p-8 prose prose-blue max-w-none">
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-blue-800 font-medium mb-2">📋 Preview Notice</p>
              <p className="text-blue-700 text-sm">
                This privacy policy applies to the preview version of StratIQX. Our comprehensive privacy policy will be available when we launch the full platform.
              </p>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-gray-800 mb-2">Personal Information</h3>
            <p className="text-gray-600 mb-4">
              When you use the StratIQX preview platform, we may collect:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4">
              <li>Contact information (name, email, phone number)</li>
              <li>Company information (company name, industry, role)</li>
              <li>Business profile data you provide during onboarding</li>
              <li>Uploaded files and documents (business data, reports)</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-2">Technical Information</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-6">
              <li>IP address and browser information</li>
              <li>Usage data and platform interactions</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-600 mb-2">We use collected information to:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6">
              <li>Provide the StratIQX preview platform services</li>
              <li>Process and analyze your business data for intelligence insights</li>
              <li>Improve our platform based on preview feedback</li>
              <li>Communicate with you about the platform and updates</li>
              <li>Ensure platform security and prevent unauthorized access</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Data Security</h2>
            <p className="text-gray-600 mb-6">
              We implement industry-standard security measures to protect your data, including encryption in transit and at rest, 
              secure data centers, and access controls. However, no method of transmission over the internet is 100% secure.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Data Sharing</h2>
            <p className="text-gray-600 mb-2">We do not sell your personal information. We may share data only in these circumstances:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6">
              <li>With your explicit consent</li>
              <li>To comply with legal obligations</li>
              <li>With trusted service providers who assist in platform operations</li>
              <li>In connection with business transfers (mergers, acquisitions)</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Your Rights</h2>
            <p className="text-gray-600 mb-2">You have the right to:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Withdraw consent for data processing</li>
              <li>Export your data in a portable format</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Cookies</h2>
            <p className="text-gray-600 mb-6">
              We use cookies to enhance your experience, analyze usage patterns, and improve our platform. 
              You can control cookie settings through your browser preferences.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. International Data Transfers</h2>
            <p className="text-gray-600 mb-6">
              Your data may be processed in countries other than your own. We ensure appropriate safeguards 
              are in place for international transfers in compliance with applicable data protection laws.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Changes to This Policy</h2>
            <p className="text-gray-600 mb-6">
              We may update this privacy policy as we develop our full platform. We will notify you of any 
              material changes and update the effective date.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Contact Us</h2>
            <p className="text-gray-600 mb-4">
              If you have questions about this privacy policy or our data practices, please contact us:
            </p>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-700"><strong>Email:</strong> privacy@stratiqx.com</p>
              <p className="text-gray-700"><strong>Address:</strong> StratIQX Privacy Officer</p>
              <p className="text-gray-700"><strong>Website:</strong> preview.stratiqx.ai</p>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                This preview privacy policy will be updated with our comprehensive policy upon full platform launch.
              </p>
            </div>

          </CardContent>
        </Card>
      </div>
    </div>
  )
}